import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, limit, onSnapshot, type QueryConstraint, type DocumentData } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import {
  SITES, DOC_CATEGORIES, isGlobal, removeDiacritics, scopedSites,
  canUploadAny, canDownload, seesPending, canTrash, canShare, canSeeTrash, canUploadNewVersion, SENSITIVE, type Member,
} from "../domain";
import { startDownload } from "../fileFetch";
import { friendly, toast } from "../errors";
import { ask, askText } from "../dialog";

type Row = DocumentData & { id: string };

/** Truy vấn phải khớp đúng firestore.rules của từng vai trò, nếu không Firestore từ chối cả truy vấn. */
function constraintsFor(m: Member): QueryConstraint[] {
  const base = [orderBy("updatedAt", "desc"), limit(300)];
  if (isGlobal(m)) return base;
  // Marketing, Nhân viên: chỉ tài liệu đã có bản duyệt (vẫn thấy khi đang có bản mới chờ duyệt)
  if (m.role === "marketing") return [where("category", "==", "IMG"), where("hasApproved", "==", true), ...base];
  const sites = [...m.sites, "AHG"];
  if (m.role === "site_mgr") return [where("siteId", "in", sites), ...base];
  return [where("staffVisible", "==", true), where("siteId", "in", sites), ...base];
}

/** Nhãn trạng thái theo góc nhìn người xem: bản đã duyệt vẫn hiệu lực khi có bản mới chờ duyệt (L24). */
function statusOf(r: Row, m: Member): { label: string; cls: string; extra?: string } {
  const pendingVisible = seesPending(m, r.siteId, r.createdBy);
  if (r.status === "uploading" || r.status === "draft") return { label: "Đang tải lên / dở", cls: "draft" };
  if (r.latestVersion > 0) {
    if (r.pendingVersion && pendingVisible) return { label: "Đã duyệt", cls: "approved", extra: `Bản v${r.pendingVersion} chờ duyệt` };
    return { label: "Đã duyệt", cls: "approved" };
  }
  if (r.status === "changes_requested") return { label: "Cần sửa", cls: "changes_requested" };
  return { label: "Chờ duyệt", cls: "pending_review" };
}

export default function Documents() {
  const { member } = useSession();
  const [rows, setRows] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);   // L57: chưa tải xong thì không báo "0 tài liệu"
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [site, setSite] = useState("");
  const [cat, setCat] = useState("");
  type Hit = { docId: string; code: string; title: string; siteId: string; category: string; version: number; approved: boolean };
  const [content, setContent] = useState<{ q: string; results: Hit[] } | null>(null);
  const [searching, setSearching] = useState(false);

  // Chỉ dựng lại truy vấn khi vai trò/cơ sở đổi (không phải mỗi lần hồ sơ cập nhật)
  const scopeKey = member ? `${member.role}|${member.sites.join(",")}` : "";
  useEffect(() => {
    if (!member) return;
    setErr(null);
    // Nhân viên: thêm tài liệu do chính mình tải lên (chờ duyệt / bị trả lại) để theo dõi và gửi bản sửa
    let main: Row[] = [], own: Row[] = [];
    const merge = () => { const seen = new Set(main.map((r) => r.id)); setRows([...main, ...own.filter((r) => !seen.has(r.id))]); };
    const off1 = onSnapshot(query(collection(db, "documents"), ...constraintsFor(member)),
      (s) => { main = s.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); setLoaded(true); }, (e) => { setErr(friendly(e)); setLoaded(true); });
    const off2 = member.role === "staff"
      ? onSnapshot(query(collection(db, "documents"), where("createdBy", "==", member.email), orderBy("updatedAt", "desc"), limit(100)),
        (s) => { own = s.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); }, () => undefined)
      : () => undefined;
    return () => { off1(); off2(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const shown = useMemo(() => {
    const k = removeDiacritics(q).toLowerCase().trim();
    return rows.filter((r) =>
      // L4: lượt tải lên chưa xong không hiện trong Kho (tiến độ đã hiện ở trang Tải lên; lượt bỏ dở Hub tự dọn)
      r.status !== "trashed" && r.status !== "uploading" &&
      (r.code || r.createdBy === member?.email) &&
      // Q4: Kế toán chỉ thấy tài liệu đã có bản duyệt (và tài liệu chính mình tải lên)
      (member?.role !== "accountant" || r.latestVersion > 0 || r.createdBy === member?.email) &&
      (!site || r.siteId === site) && (!cat || r.category === cat) &&
      (!k || removeDiacritics(`${r.code} ${r.title} ${r.subcategory} ${(r.tags ?? []).join(" ")}`).toLowerCase().includes(k)));
  }, [rows, q, site, cat, member?.email]);

  const nav = useNavigate();
  const open = (id: string, v?: number) => nav(v ? `/xem/${id}?v=${v}` : `/xem/${id}`);
  const searchInside = async () => {
    const text = q.trim();
    if (text.length < 2) return;
    setSearching(true);
    try { const r = await call<object, { results: Hit[] }>("searchContent")({ q: text }); setContent({ q: text, results: r.results }); }
    catch (e) { toast(friendly(e)); }
    setSearching(false);
  };
  const download = async (id: string) => {
    try {
      const r = await call<{ docId: string; download: boolean }, { url: string }>("getDocumentLink")({ docId: id, download: true });
      await startDownload(r.url);
    } catch (e) { toast(friendly(e)); }
  };

  const cancel = async (r: Row) => {
    if (!await ask({ title: "Xóa lượt tải lên dở?", danger: true, confirm: "Xóa", message: <><b>{r.title}</b><br />File chưa tải lên xong sẽ bị bỏ.</> })) return;
    try { await call("cancelDocumentUpload")({ docId: r.id }); toast("Đã xóa lượt tải lên dở.", "ok"); } catch (e) { toast(friendly(e)); }
  };

  const trash = async (r: Row) => {
    const reason = await askText({ title: `Chuyển ${r.code} vào thùng rác?`, danger: true, confirm: "Chuyển vào thùng rác",
      message: <><b>{r.title}</b><br />Tài liệu ẩn khỏi Kho ngay. Khôi phục được trong 30 ngày, sau đó Hub tự xóa vĩnh viễn.</>,
      input: { label: "Lý do (không bắt buộc)", placeholder: "vd: tải nhầm, trùng tài liệu…" } });
    if (reason === null) return;
    try { await call("trashDocument")({ docId: r.id, reason }); toast(`Đã chuyển ${r.code} vào thùng rác.`, "ok"); } catch (e) { toast(friendly(e)); }
  };

  if (!member) return null;
  const seeTrash = canSeeTrash(member);
  const siteOpts = isGlobal(member) || member.role === "marketing" ? Object.keys(SITES) : [...scopedSites(member), "AHG"];
  // L42: Nhân viên không có các loại không được xem (Hợp đồng, Tài chính, Nhân sự)
  const catOpts = member.role === "marketing" ? ["IMG"] : Object.keys(DOC_CATEGORIES).filter((c) => member.role !== "staff" || !SENSITIVE.includes(c));

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div><h1>Kho tài liệu</h1><p className="sub">{loaded ? `${shown.length} tài liệu` : "Đang tải…"} · chỉ hiện phần bạn được quyền xem</p></div>
        {seeTrash && <Link className="btn" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to="/documents/trash">Thùng rác</Link>}
        {canUploadAny(member) && <Link className="btn primary" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to="/documents/upload">+ Tải lên tài liệu</Link>}
      </div>
      <div className="row" style={{ marginBottom: 16 }}>
        <div className="field"><label htmlFor="q">Tìm (không cần dấu)</label><input id="q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void searchInside()} placeholder="vd: hop dong thue" /></div>
        {siteOpts.length > 1 && <div className="field" style={{ flex: "0 1 200px" }}><label htmlFor="s">Cơ sở</label>
          <select id="s" value={site} onChange={(e) => setSite(e.target.value)}><option value="">Tất cả</option>
            {siteOpts.map((k) => <option key={k} value={k}>{SITES[k]}</option>)}</select></div>}
        {catOpts.length > 1 && <div className="field" style={{ flex: "0 1 240px" }}><label htmlFor="c">Loại</label>
          <select id="c" value={cat} onChange={(e) => setCat(e.target.value)}><option value="">Tất cả</option>
            {catOpts.map((k) => <option key={k} value={k}>{DOC_CATEGORIES[k]}</option>)}</select></div>}
      </div>
      {q.trim().length >= 2 && (
        <div className="row" style={{ margin: "-6px 0 14px", gap: 10, alignItems: "center" }}>
          <button className="btn" disabled={searching} onClick={searchInside}>{searching ? "Đang tìm trong nội dung…" : `Tìm "${q.trim()}" trong nội dung file`}</button>
          <span className="muted" style={{ fontSize: 13 }}>PDF, Word, Excel, PowerPoint. Gõ có dấu cho kết quả chính xác hơn.</span>
        </div>
      )}
      {content && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: content.results.length ? 10 : 0 }}>
            <b>{content.results.length ? `${content.results.length} tài liệu có chứa "${content.q}"` : `Không có tài liệu nào chứa "${content.q}" trong nội dung`}</b>
            <button className="btn ghost" aria-label="Đóng kết quả" onClick={() => setContent(null)}>✕</button>
          </div>
          {content.results.length > 0 && <ul className="upList">{content.results.map((h) => (
            <li key={h.docId} style={{ alignItems: "center" }}>
              <span className="code">{h.code}</span>
              <div className="upBody"><span className="upName">{h.title}</span>
                <span className="muted" style={{ fontSize: 12.5 }}>{SITES[h.siteId]} · {DOC_CATEGORIES[h.category]}{h.approved ? "" : ` · bản v${h.version} chờ duyệt`}</span></div>
              <button className="btn" onClick={() => open(h.docId, h.approved ? undefined : h.version)}>Mở</button>
            </li>))}</ul>}
          <p className="muted" style={{ fontSize: 12.5, margin: "10px 0 0" }}>Ảnh và video không tìm được theo nội dung. File vừa tải lên cần vài phút để Google đọc xong.</p>
        </div>
      )}
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      <div className="card tableWrap" style={{ padding: 0 }}>
        <table className="table stack">
          <thead><tr><th>Mã</th><th>Tiêu đề</th><th>Cơ sở</th><th>Loại</th><th>Trạng thái</th><th>Hiệu lực đến</th><th></th></tr></thead>
          <tbody>
            {shown.map((r) => {
              const st = statusOf(r, member);
              const unfinished = st.cls === "draft";
              const viewable = !unfinished && (r.latestVersion > 0 || seesPending(member, r.siteId, r.createdBy));
              const mine = r.createdBy === member.email;
              const canRevise = !unfinished && !r.pendingVersion && canUploadNewVersion(member, r)
                && (r.status !== "changes_requested" || mine || member.role === "admin" || (member.role === "site_mgr" && member.sites.includes(r.siteId)));
              const pendingNote = r.reviewNote && seesPending(member, r.siteId, r.createdBy) && !r.pendingVersion;
              return (
                <tr key={r.id}>
                  <td className="code cardHead">{r.code ?? "—"}</td>
                  <td>{r.title}<div style={{ fontSize: 12, color: "var(--muted)" }}>{r.subcategory} · {r.ownerName}</div>
                    {pendingNote ? <div className="note">{r.status === "changes_requested" ? "Bị trả lại" : "Bản mới bị trả lại"}{r.reviewedBy ? ` (${r.reviewedBy})` : ""}: {r.reviewNote}</div> : null}</td>
                  <td data-label="Cơ sở">{SITES[r.siteId]}</td>
                  <td data-label="Loại">{DOC_CATEGORIES[r.category]}</td>
                  <td data-label="Trạng thái"><span className={`pill ${st.cls}`}>{st.label}</span>{st.extra && <div className="muted" style={{ marginTop: 4 }}>{st.extra}</div>}</td>
                  <td data-label="Hiệu lực đến">{r.expiresAt ? r.expiresAt.toDate().toLocaleDateString("vi-VN") : "—"}</td>
                  <td className="row actions" style={{ gap: 6 }}>
                    {viewable && <button className="btn" onClick={() => open(r.id)}>Mở</button>}
                    {viewable && canDownload(member, r.category) && <button className="btn" onClick={() => download(r.id)}>Tải về</button>}
                    {unfinished && (mine || member.role === "admin") && <button className="btn" onClick={() => cancel(r)}>Xóa</button>}
                    {r.latestVersion > 0 && canShare(member, r) && <Link className="btn" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to={`/documents/${r.id}/share`}>Chia sẻ</Link>}
                    {!unfinished && canTrash(member, r) && <button className="btn" onClick={() => trash(r)}>Xóa</button>}
                    {canRevise && <Link className="btn" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to={`/documents/upload?replace=${r.id}`}>{r.status === "changes_requested" ? "Gửi bản sửa" : "Bản mới"}</Link>}
                  </td>
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={7} style={{ color: "var(--muted)" }}>{err ? "Chưa tải được danh sách." : loaded ? "Chưa có tài liệu." : "Đang tải danh sách…"}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
